/**
 * Reusable loading skeleton component
 * Displays animated loading placeholders for content
 */

interface LoadingSkeletonProps {
  variant?: 'card' | 'metric' | 'chart' | 'table'
  count?: number
}

const LoadingSkeleton = ({ variant = 'card', count = 1 }: LoadingSkeletonProps) => {
  const skeletonStyles: React.CSSProperties = {
    backgroundColor: '#e2e8f0',
    borderRadius: '8px',
    animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
  }

  const renderCardSkeleton = () => (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    }}>
      <div style={{ ...skeletonStyles, height: '24px', width: '60%', marginBottom: '16px' }} />
      <div style={{ ...skeletonStyles, height: '48px', width: '40%', marginBottom: '12px' }} />
      <div style={{ ...skeletonStyles, height: '16px', width: '80%' }} />
    </div>
  )

  const renderMetricSkeleton = () => (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '16px',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
    }}>
      <div style={{ ...skeletonStyles, height: '16px', width: '70%', marginBottom: '12px' }} />
      <div style={{ ...skeletonStyles, height: '32px', width: '50%', marginBottom: '8px' }} />
      <div style={{ ...skeletonStyles, height: '12px', width: '60%' }} />
    </div>
  )

  const renderChartSkeleton = () => (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    }}>
      <div style={{ ...skeletonStyles, height: '20px', width: '40%', marginBottom: '20px' }} />
      <div style={{ ...skeletonStyles, height: '200px', width: '100%' }} />
    </div>
  )

  const renderTableSkeleton = () => (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    }}>
      <div style={{ ...skeletonStyles, height: '20px', width: '30%', marginBottom: '16px' }} />
      {[...Array(5)].map((_, i) => (
        <div key={i} style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
          <div style={{ ...skeletonStyles, height: '16px', width: '25%' }} />
          <div style={{ ...skeletonStyles, height: '16px', width: '25%' }} />
          <div style={{ ...skeletonStyles, height: '16px', width: '25%' }} />
          <div style={{ ...skeletonStyles, height: '16px', width: '25%' }} />
        </div>
      ))}
    </div>
  )

  const renderSkeleton = () => {
    switch (variant) {
      case 'metric':
        return renderMetricSkeleton()
      case 'chart':
        return renderChartSkeleton()
      case 'table':
        return renderTableSkeleton()
      case 'card':
      default:
        return renderCardSkeleton()
    }
  }

  return (
    <>
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
      {[...Array(count)].map((_, i) => (
        <div key={i} style={{ marginBottom: i < count - 1 ? '16px' : '0' }}>
          {renderSkeleton()}
        </div>
      ))}
    </>
  )
}

export default LoadingSkeleton
